// ABOUTME: The mobile reel's top chrome (SPEC-26 Phase 4b) — top scrim, centred wordmark, menu chip.
// ABOUTME: The hamburger reveals the same three concertina sections the desktop rail shows.

import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { GLYPHS, CHIP_BADGE } from './railGeometry';
import { itemQuantity } from '../../utils/cartMath';
import RowActions from './RowActions';
import ChipBadge from './ChipBadge';
import { useDeviceProfile, useViewportSize } from '../Projectionist/useViewportSize';
import {
  MOBILE_UNIT, mu, muMin, safeTop, safeRight, MIN_TEXT_PX,
  WORDMARK, MENU_CHIP, MENU_GLYPH, MENU_GAP, TOP_SCRIM, MOBILE_PANEL, MOBILE_CONSENT, MOBILE_CONSENT_TEXT,
  TILE_COMPACT_SCALE, ITEMS_PER_ROW, ROWS_BEFORE_SCROLL, ITEM_ART_H, ITEM_TEXT_BLOCK_H, panelMaxHeight, mobileUnitFor,
  NAME_LINES, NAME_LINE_HEIGHT, nameBlockHeight, clearedPanelWidth, mobilePanelContentWidth,
} from './mobileGeometry';
import ConsentPanel from './ConsentPanel';
import LogoResetButton from './LogoResetButton'; // SPEC-74: the wordmark is a button only when `onLogoReset` is passed
import MobilePanelScrim from './MobilePanelScrim';

// Same rule as the desktop panel (client feedback, 2026-08-24): no "N more items" summary and no
// "See full …" escalation to a second, full-height view — every item renders in the same 2-up grid,
// stepping its tile size down past ITEMS_PER_ROW items and scrolling past ROWS_BEFORE_SCROLL rows.

/**
 * The 88x88 chip. Identical treatment to the desktop rail header (`reel.chrome.iconChip`,
 * `backdrop-blur-reel-panelDense`, full radius) — verified against 45:3262, which returns exactly
 * the same three values as desktop's 45:4840. Only the glyph and the corner differ.
 *
 * `reel-touch-target` grows the hit area to 44px without touching the visual box: at the design's
 * own proportions an 88 authored px chip renders at 28px on a 390px handset, which is right
 * visually and too small to hit. See index.css.
 */
function MobileChip({ glyph, label, onClick, expanded, children, glyphStyle }) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-expanded={expanded}
      onClick={onClick}
      className="reel-touch-target relative flex items-center justify-center bg-reel-chrome-iconChip backdrop-blur-reel-panelDense rounded-full hover:bg-white/10 active:bg-white/10 transition-colors shrink-0"
      style={{ width: mu(MENU_CHIP.size), height: mu(MENU_CHIP.size) }}
    >
      <img
        src={glyph.src}
        alt=""
        aria-hidden="true"
        style={glyphStyle || { width: mu(glyph.w), height: mu(glyph.h) }}
      />
      {children}
    </button>
  );
}

function Hairline({ width = mu(MOBILE_PANEL.contentW) }) {
  return (
    <div
      className="bg-reel-chrome-textPrimary/30 shrink-0"
      style={{ width, height: mu(MOBILE_PANEL.ruleH) }}
    />
  );
}

function PanelButton({ children, onClick, width, widthCss }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="reel-touch-target relative bg-reel-cta-secondaryBg backdrop-blur-reel-panelButton hover:bg-white/15 active:bg-white/15 transition-colors text-reel-chrome-textPrimary font-reel-body flex items-center justify-center shrink-0"
      style={{
        width: widthCss || mu(width),
        height: mu(MOBILE_PANEL.ctaH),
        borderRadius: mu(MOBILE_PANEL.radius),
        fontSize: muMin(MOBILE_PANEL.text.button, MIN_TEXT_PX),
      }}
    >
      {children}
    </button>
  );
}

/** One product tile in the grid. Same parts as the desktop tile, laid out by flow rather than
 *  by the desktop panel's authored absolute offsets — those offsets are measurements of a 3840-space
 *  composition and do not describe anything on this frame. `tileW` scales down past ITEMS_PER_ROW
 *  items; the text stays at its authored size at every step, same rule as desktop. */
function MobileTile({
  item, priceDisplay, linePriceDisplay, onSelect, onToggleSaved, onRemove,
  onIncrement, onDecrement, saved = false, removable = false, tileW = MOBILE_PANEL.tileW, maxSide,
}) {
  const quantity = itemQuantity(item);
  return (
    // Full-width since 2026-09-01, same change as the desktop tile: this fills a half-panel `1fr`
    // column now instead of being a fixed-width island. Mobile had the gutter too — at ≤2 items the
    // derived tileW (511) already tiles the 1035.333 content column exactly, but past
    // ITEMS_PER_ROW the compact scale drops the row to 2 x 408.8 + 10.667 = 828.27, leaving ~207
    // authored px dead down the right. Same defect, same fix.
    <div className="w-full min-w-0">
      {/* Artwork stays exactly `tileW` square and centres in the wider column — the client turned
          down growing the watches (a taller row = fewer items before scrolling), and pinning the
          width keeps the ×N pill on the artwork's corner rather than the column's. */}
      <button
        type="button"
        onClick={() => onSelect?.(item)}
        className="reel-touch-target relative block mx-auto"
        // `maxSide` (a panel narrowed to clear the hamburger): the artwork yields to a column narrower than its authored size.
        style={maxSide ? { width: `min(${mu(tileW)}, ${maxSide})`, height: `min(${mu(tileW)}, ${maxSide})` } : { width: mu(tileW), height: mu(tileW) }}
      >
        <img
          src={item.image_url || item.image_uri}
          alt={item.title || 'Product'}
          className="w-full h-full object-contain"
        />
        {/* ×N pill for a multi-quantity line — same treatment as the desktop tile, and the same
            authored CHIP_BADGE numbers, resolved through the mobile unit. The 88 chip these are
            sized against is shared across both compositions (see mobileGeometry's MENU_CHIP), so
            one authored table is correct for both rather than a coincidence. */}
        {quantity > 1 && (
          <span
            aria-hidden="true"
            className="absolute top-1 right-1 rounded-full bg-reel-chrome-textPrimary text-reel-chrome-panel font-bold flex items-center justify-center"
            style={{
              minWidth: muMin(CHIP_BADGE.size, CHIP_BADGE.minSize),
              height: muMin(CHIP_BADGE.size, CHIP_BADGE.minSize),
              paddingLeft: mu(CHIP_BADGE.padX),
              paddingRight: mu(CHIP_BADGE.padX),
              fontSize: muMin(CHIP_BADGE.font, CHIP_BADGE.minFont),
            }}
          >
            ×{quantity}
          </span>
        )}
      </button>
      {/* Same reserved two-line title box as the desktop tile, for the same client feedback
          (2026-09-01): each tile is an independent grid item, so a name that wrapped dropped its
          price and action strip a line below the neighbouring tile's and the row read as
          misaligned. Explicit line-height, a min-height of exactly NAME_LINES of them, and a
          2-line clamp on top.
          The clamp is a HARD limit, not an unreachable safety net: `-webkit-line-clamp` truncates
          the third line and renders a visible ellipsis. Headroom is measured on desktop (visual
          pass, 2026-09-01: ~26 authored px, roughly two characters, on the tightest of the client's
          four titles) and is tighter here, since this frame sets the name at its own larger body
          token. Kept at two lines deliberately all the same: NAME_LINES feeds ITEM_TEXT_BLOCK_H and
          `visibleGridH`, and the scroll-paging model needs a tile row whose height cannot grow.
          `leading-tight` is gone because the geometry has to be able to multiply the number.
          Mobile authors no name-column width of its own (the frames never draw a cart panel — see
          mobileGeometry's MOBILE_PANEL note), so there is no mobile counterpart to desktop's
          nameW widening; the title has always run the tile's full width here. */}
      <p
        className="m-0 text-reel-chrome-textPrimary font-reel-heading font-bold"
        style={{
          fontSize: muMin(MOBILE_PANEL.text.product, MIN_TEXT_PX),
          marginTop: mu(MOBILE_PANEL.rowGap),
          lineHeight: NAME_LINE_HEIGHT,
          minHeight: nameBlockHeight(),
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: NAME_LINES,
          overflow: 'hidden',
        }}
      >
        {item.title || 'Untitled'}
      </p>
      <p
        className="m-0 text-reel-chrome-textPrimary font-reel-body"
        style={{ fontSize: muMin(MOBILE_PANEL.text.price, MIN_TEXT_PX), marginTop: mu(MOBILE_PANEL.rowGap / 2) }}
      >
        {(linePriceDisplay || priceDisplay)(item)}
      </p>
      {/* Same strip as the desktop tile, at this frame's own scale — bag rows get heart · − · + · ✕,
          favourites rows the heart alone. Decided by the panel KIND via `removable`, never by which
          handlers happen to be wired. */}
      <RowActions
        item={item}
        quantity={quantity}
        saved={removable ? saved : true}
        showQuantity={removable}
        showRemove={removable}
        unit={mu}
        heartW={MOBILE_PANEL.heartW}
        heartH={MOBILE_PANEL.heartH}
        iconSize={MOBILE_PANEL.heartH}
        gap={MOBILE_PANEL.actionGap}
        onToggleSaved={onToggleSaved}
        onIncrement={onIncrement}
        onDecrement={onDecrement}
        onRemove={onRemove}
        buttonClass="reel-touch-target relative"
        style={{ marginTop: mu(MOBILE_PANEL.rowGap / 2) }}
      />
    </div>
  );
}

/**
 * Scroll a grid by exactly one visible page — the mobile twin of IconRail's `scrollGridByPage`.
 *
 * One named function for the agent's `scroll_panel` effect AND the guest's chevron tap, so the two
 * cannot drift into two different ideas of "a page" (2026-09-01). `behavior` is passed in rather
 * than read here because only the component knows the guest's `prefers-reduced-motion` answer.
 *
 * The element's own `clientHeight` is the real page, preferred over the authored `visibleGridH`
 * because the grid is a shrinkable flex item now (see MobilePanel's container) — on a short handset
 * its visible box is whatever is left once the header and the checkout footer have taken theirs.
 * `visibleGridH` stays as the fallback for jsdom, which measures everything as 0.
 *
 * @param {HTMLElement|null} el @param {'up'|'down'} direction
 * @param {number} pageHeight one visible grid height in authored-derived px, used if unmeasurable
 * @param {ScrollBehavior} behavior 'auto' for a guest who asked the OS for less motion
 */
function scrollGridByPage(el, direction, pageHeight, behavior) {
  if (!el) return;
  const page = el.clientHeight || pageHeight;
  el.scrollBy({ top: direction === 'up' ? -page : page, behavior });
}

/**
 * Same scroll affordance as the desktop panel — shown only on the edge it points toward has more
 * content.
 *
 * A real `<button>` since 2026-09-01, for the same reason as desktop's: as a `pointer-events-none`
 * div only the agent could move the list, and a guest who tapped the chevron they could plainly see
 * got nothing. It opts into `reel-touch-target` like every other control on this frame — the disc is
 * 32 authored px, which is under the 44px minimum on any real handset.
 */
const ScrollHint = React.forwardRef(function ScrollHint({ direction, onClick }, ref) {
  const Icon = direction === 'up' ? ChevronUp : ChevronDown;
  return (
    <button
      ref={ref}
      type="button"
      aria-label={direction === 'up' ? 'Scroll up' : 'Scroll down'}
      onClick={onClick}
      className={`reel-touch-target absolute left-1/2 -translate-x-1/2 ${direction === 'up' ? 'top-0' : 'bottom-0'} z-10 flex items-center justify-center rounded-full bg-reel-chrome-panel/90 backdrop-blur-sm shadow-md animate-bounce active:bg-reel-chrome-panel`}
      // Inline `pointerEvents` for the same jsdom reason documented on the chrome's root below.
      // `absolute` already establishes the positioned box `.reel-touch-target::after` needs, so it
      // deliberately does NOT also carry `relative` the way the in-flow controls here do.
      style={{ pointerEvents: 'auto', width: 32, height: 32, [direction === 'up' ? 'top' : 'bottom']: -12 }}
    >
      <Icon size={18} aria-hidden="true" className="text-reel-chrome-textPrimary" />
    </button>
  );
});

/**
 * An expanded section. Structurally the desktop panel (glyph absorbed into the panel and still the
 * toggle, title with count, rule, item grid, Subtotal, CTA) in the mobile panel's own dimensions —
 * see MOBILE_PANEL's note on where those come from.
 */
function MobilePanel({
  kind, glyph, title, label, items, priceDisplay, linePriceDisplay, totalLabel, totalValue,
  onCollapse, onCheckout,
  onSelectItem, onToggleSaved,
  onRemoveItem, unitCount, onIncrementItem, onDecrementItem, savedIds,
  reducedMotion = false, scrollCommand, onScrollStateChange, clearsMenu = false,
}) {
  const isCart = kind === 'CART';
  // `clearsMenu`: the top-left hamburger is on screen (collection_menu), so the panel is capped to start past it and everything fixed
  // to the authored content column follows the panel. Off, `widthCss` is undefined and no style differs from the authored panel.
  const widthCss = clearsMenu ? clearedPanelWidth() : undefined;
  const contentW = mobilePanelContentWidth(widthCss);

  const compact = items.length > ITEMS_PER_ROW;
  const scale = compact ? TILE_COMPACT_SCALE : 1;
  const tileW = MOBILE_PANEL.tileW * scale;
  const rows = Math.ceil(items.length / ITEMS_PER_ROW);
  const scrollable = rows > ROWS_BEFORE_SCROLL;
  const rowGap = MOBILE_PANEL.tileGap * scale;
  // Only the artwork scales — see mobileGeometry's ITEM_ART_H / ITEM_TEXT_BLOCK_H note. This is now
  // the jsdom-only fallback for scroll paging (`scrollGridByPage` prefers the measured
  // `clientHeight`), but it was the grid's max-height until 2026-09-01 and it cropped rows.
  const itemH = ITEM_ART_H * scale + ITEM_TEXT_BLOCK_H;
  const visibleGridH = ROWS_BEFORE_SCROLL * itemH + (ROWS_BEFORE_SCROLL - 1) * rowGap;

  const gridRef = useRef(null);
  const prevCountRef = useRef(items.length);
  const upHintRef = useRef(null);
  const downHintRef = useRef(null);
  // Which chevron the guest last tapped — see the focus-rescue effect below.
  const lastHintRef = useRef(null);
  const [scrollHint, setScrollHint] = useState({ up: false, down: false });

  // Same contract as the desktop panel's `readScrollState`, including the upstream report added
  // 2026-09-01 — see IconRail.js for why the chevrons and the agent must read one measurement, and
  // why the drag-rate `onScroll` traffic is safe (the reporter dedupes).
  const readScrollState = () => {
    const el = gridRef.current;
    const overflowing = !!el && el.scrollHeight > el.clientHeight + 4;
    const up = overflowing && el.scrollTop > 4;
    const down = overflowing && el.scrollTop + el.clientHeight < el.scrollHeight - 4;
    if (el) setScrollHint({ up, down });
    onScrollStateChange?.({ panel: kind, scrollable: overflowing, atTop: !up, atBottom: !down });
  };

  // `items.length` in the deps so an add/remove re-reports — see the desktop twin.
  useEffect(() => { readScrollState(); }, [items.length, scrollable]);

  // Forget this panel's cached state on unmount, so the next open always re-reports — the backend
  // pops its own entry when the panel closes, and a longer-lived cache would leave the two
  // disagreeing. Full reasoning in IconRail.js and in usePanelScrollReporter's header.
  useEffect(() => () => onScrollStateChange?.({ panel: kind, forget: true }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  useEffect(() => {
    const el = gridRef.current;
    if (el && items.length > prevCountRef.current) {
      el.scrollTo({ top: el.scrollHeight, behavior: reducedMotion ? 'auto' : 'smooth' });
    }
    prevCountRef.current = items.length;
  }, [items.length, reducedMotion]);

  // The agent's `scroll_panel` tool — same contract as the desktop rail's RailPanel.
  useEffect(() => {
    if (!scrollCommand || scrollCommand.panel !== kind) return;
    scrollGridByPage(gridRef.current, scrollCommand.direction, visibleGridH, reducedMotion ? 'auto' : 'smooth');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollCommand?.nonce]);

  // The guest's own path to the same action (2026-09-01) — same helper, same page height, and the
  // same reduced-motion answer the agent path honours, so a tap and a spoken "scroll down" behave
  // identically. Hoisted out of the JSX rather than inlined on the chevron.
  const handleHintClick = (direction) => {
    lastHintRef.current = direction;
    scrollGridByPage(gridRef.current, direction, visibleGridH, reducedMotion ? 'auto' : 'smooth');
  };

  // Keyboard rescue — same contract as the desktop rail's. Tapping the last chevron scrolls to the
  // end of the list and unmounts the control that was just activated, which drops focus to <body>;
  // hand it to the surviving sibling instead. Guarded on focus having actually been lost, so it is
  // never yanked away from a guest who moved on while the scroll was still settling. It matters on
  // this frame too: a handset with a paired keyboard, or a switch/AT user, hits exactly the same
  // dead end that a mouse user never notices.
  useEffect(() => {
    const last = lastHintRef.current;
    if (!last) return;
    if (last === 'up' ? scrollHint.up : scrollHint.down) return;
    lastHintRef.current = null;
    if (typeof document === 'undefined' || document.activeElement !== document.body) return;
    (last === 'up' ? downHintRef : upHintRef).current?.focus();
  }, [scrollHint.up, scrollHint.down]);

  return (
    <motion.div
      key={kind}
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      // The concertina's displacement IS the interaction, so it is the one animation worth having
      // here — and the one worth dropping outright for a guest who has asked the OS for less motion.
      // A height animation on a near-full-screen panel is exactly the kind of movement that setting
      // exists for.
      transition={reducedMotion ? { duration: 0 } : { duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
      className="overflow-hidden shrink-0"
      style={{ width: widthCss || mu(MOBILE_PANEL.w) }}
    >
      {/* `custom-scrollbar` + `overflow-x-hidden` — the desktop panel's fix, applied here for the
          same reason (2026-09-01). This container was the one place in the chrome still exposing
          system scrollbars: the inner grid hid its own, this outer box never did, and
          `overflow-y-auto` alone promotes `overflow-x` from `visible` to `auto`, which is where the
          second, horizontal bar came from. Only the bag ever overflows it, because only the bag
          carries the Subtotal + checkout footer under the grid. The chevrons overhang the GRID
          vertically by 12px, inside MOBILE_PANEL.pad (53.333 authored px) and below the
          glyph/title/rule stack, so clipping the horizontal axis cannot reach them.

          It stays `overflow-y-auto` rather than becoming `overflow-hidden` only as a last-resort
          valve: the grid absorbs the deficit now (see `min-h-0` on its wrapper), so this is not
          expected to scroll, but a viewport too short even for a zero-height grid must degrade to
          a scroll rather than to silently clipped content. */}
      <div
        className="bg-reel-chrome-panel backdrop-blur-reel-chrome flex flex-col items-start overflow-y-auto overflow-x-hidden custom-scrollbar relative"
        style={{
          padding: mu(MOBILE_PANEL.pad),
          borderRadius: mu(MOBILE_PANEL.radius),
          gap: mu(MOBILE_PANEL.rowGap),
          maxHeight: panelMaxHeight(),
        }}
      >
        <button
          type="button"
          aria-label={label}
          aria-expanded="true"
          onClick={onCollapse}
          className="reel-touch-target relative block hover:opacity-70 active:opacity-70 transition-opacity"
        >
          <img
            src={glyph.src}
            alt=""
            aria-hidden="true"
            style={{ width: mu(glyph.w), height: mu(glyph.h) }}
          />
        </button>

        {/* Explicit close affordance — same contract as the desktop rail's RailPanel: same row as
            the title, baseline-aligned, per the client's Figma frame (X beside "Shopping Bag (4)"). */}
        <div className="flex items-center justify-between" style={{ width: contentW }}>
          <h2
            className="m-0 text-reel-chrome-textPrimary font-reel-body"
            style={{ fontSize: muMin(MOBILE_PANEL.text.title, MIN_TEXT_PX) }}
          >
            {/* Unit count, not row count: two lines of two each is a bag of four. */}
            {`${title} (${unitCount ?? items.length})`}
          </h2>
          <button
            type="button"
            aria-label={`Close ${title}`}
            onClick={onCollapse}
            className="reel-touch-target relative shrink-0 hover:opacity-70 active:opacity-70 transition-opacity text-reel-chrome-textPrimary"
            style={{ width: mu(MOBILE_PANEL.heartH), height: mu(MOBILE_PANEL.heartH) }}
          >
            <X aria-hidden="true" style={{ width: mu(MOBILE_PANEL.heartH), height: mu(MOBILE_PANEL.heartH) }} />
          </button>
        </div>

        <Hairline width={contentW} />

        {items.length === 0 ? (
          <p
            className="m-0 text-reel-chrome-textDim font-reel-body"
            style={{ fontSize: muMin(MOBILE_PANEL.text.secondary, MIN_TEXT_PX) }}
          >
            {isCart ? 'Your shopping bag is empty.' : 'No items saved yet.'}
          </p>
        ) : (
          /* `min-h-0` + an always-scrollable grid, same fix and same reasoning as the desktop rail
             (reported 2026-09-01): a flex item's automatic minimum size is its content, so when the
             panel hit `panelMaxHeight()` nothing inside would shrink and the checkout CTA was
             pushed out of the container. Opting the grid wrapper out of that floor — and the grid
             itself out of it, via its own `overflow-y: auto` — makes the LIST absorb the deficit,
             which is the one part of the panel that has chevrons and an agent path to reach the
             rest of it. Nothing is hand-computed; the browser subtracts the header and footer.
             The overflow class is unconditional now, not gated on the `scrollable` row count: a
             shrunk grid overflows well before that flag flips, and the rows would otherwise be cut
             off with no affordance at all. A plain block comment rather than the JSX kind: this
             position is a bare ternary branch, where braces would parse as an object literal. */
          <div className="relative w-full flex flex-col min-h-0">
            <div
              ref={gridRef}
              onScroll={readScrollState}
              className="overflow-y-auto custom-scrollbar"
              style={{
                display: 'grid',
                // Equal half-panel columns, same client redline as the desktop rail (2026-09-01).
                // `1fr` rather than a computed `(contentW - gap) / 2`: the wrapper already stretches
                // this to the panel's content width, and that width is `mu(MOBILE_PANEL.w)` minus
                // padding, which tracks the viewport — a constant would not.
                gridTemplateColumns: `repeat(${ITEMS_PER_ROW}, 1fr)`,
                columnGap: mu(rowGap),
                rowGap: mu(rowGap),
                // NO `maxHeight: mu(visibleGridH)` here. It used to cap the grid at a HAND-COMPUTED
                // two-row height, which is what the client saw as "cropping with a lot of items"
                // (2026-09-01): measured on a 390x844 handset, the cap held the grid at 378px while
                // the panel's own ceiling left room for 443, so the second row was sliced through
                // the middle — name visible, price and action strip gone — with 65px of unused
                // panel below it. Two separate reasons it was wrong: it fought the `min-h-0` rule
                // right above (which exists so the BROWSER subtracts the header and footer), and
                // `itemH` under-measured a row by ~12% anyway (see mobileGeometry's ITEM_ART_H /
                // ITEM_TEXT_BLOCK_H split), so even as an approximation it cropped. The panel's
                // `maxHeight: panelMaxHeight()` is the real ceiling and still keeps the frosted
                // edge off the caption bar; this grid just uses whatever that leaves.
              }}
            >
              {items.map((item, idx) => (
                <MobileTile
                  key={item.id || item.sku || idx}
                  item={item}
                  tileW={tileW}
                  priceDisplay={priceDisplay}
                  linePriceDisplay={linePriceDisplay}
                  onSelect={onSelectItem}
                  onToggleSaved={onToggleSaved}
                  onRemove={onRemoveItem}
                  onIncrement={onIncrementItem}
                  onDecrement={onDecrementItem}
                  saved={!!savedIds?.has(item.id)}
                  removable={isCart}
                  maxSide={widthCss && `calc((${contentW} - ${mu(rowGap)}) / 2)`}
                />
              ))}
            </div>
            {/* Measured overflow, not the `scrollable` row count — the grid shrinks now, so it can
                overflow below that threshold and still needs an affordance. */}
            {scrollHint.up && (
              <ScrollHint ref={upHintRef} direction="up" onClick={() => handleHintClick('up')} />
            )}
            {scrollHint.down && (
              <ScrollHint ref={downHintRef} direction="down" onClick={() => handleHintClick('down')} />
            )}
          </div>
        )}

        {/* Cart-only, for the same reason as desktop: the Figma favourites frame shows a Subtotal
            because its mock content was copy-pasted from the bag, and a favourites list has
            nothing to total (SPEC-26 §0e). */}
        {isCart && items.length > 0 && (
          <>
            <Hairline width={contentW} />
            <div
              className="flex justify-between items-baseline text-reel-chrome-textPrimary font-reel-heading font-bold"
              style={{ width: contentW, fontSize: muMin(MOBILE_PANEL.text.title, MIN_TEXT_PX) }}
            >
              <span>{totalLabel}</span>
              <span>{totalValue}</span>
            </div>
            {/* SPEC-46 Rev 7 (D-23): one button again. It always renders and always sends
                checkout_request — the overlay mounts once the offer lands, so there is no
                second "open" step to branch on. Where this deployment has no publishable key,
                `onCheckout` (useCheckout's `requestCheckout`) is a silent no-op (JC AC-30): the
                gap is invisible to the guest rather than removing the CTA. */}
            <PanelButton width={MOBILE_PANEL.contentW} widthCss={clearsMenu ? contentW : undefined} onClick={onCheckout}>
              Continue to Checkout
            </PanelButton>
          </>
        )}
      </div>
    </motion.div>
  );
}

/**
 * The mobile top chrome.
 *
 * **What the frames author, and what they don't.** All seven mobile frames carry exactly one
 * control: an 88x88 chip at (1091, 106) whose glyph is three strokes — a hamburger — and whose node
 * is `Cookies Consetina v2`, holding the same clipped privacy panel as desktop's third rail item.
 * So the artwork says "menu" and the node says "privacy", and the design authors no mobile home for
 * the bag or the favourites at all. Resolved with the client (2026-08-13): the chip is a **menu**,
 * and it reveals the same three sections the desktop rail shows. Dropping bag and favourites on
 * phones would have removed two working, agent-drivable features from the composition rather than
 * translating it.
 *
 * The revealed sections are the desktop concertina, not a new pattern: tapping one expands it in
 * place and displaces those below — privacy included, since 2026-08-24 (it was the centred
 * `PreferencesCenter` modal before that; see SPEC-26 OQ-R4 follow-up). Because all three read
 * App.js's single `overlay` slot, the panels stay mutually exclusive and the agent can still drive
 * them (R.7) with no extra plumbing — the same behaviour the desktop rail gets for free.
 *
 * `alwaysExpanded` (2026-09-14, Ophion demo only — see App.js) drops the hamburger for a tenant
 * that wants the three chips permanently visible, no menu step in between. The hamburger and its
 * `menuOpen` state are otherwise untouched, so any other tenant keeps the original behaviour.
 */
function MobileChrome({
  expanded, onToggle, cartItems, wishlistItems, priceDisplay, linePriceDisplay, cartTotalDisplay,
  cartUnitCount, onRemoveCartLine, onIncrementCartLine, onDecrementCartLine,
  onCheckout, onSelectItem,
  onToggleSaved, privacyLabel,
  consent, onSaveConsent, onOpenPrivacyPolicy,
  recording = false, showIntro = false, wordmarkSrc, brandName = '', scrollCommand = null,
  onScrollStateChange, alwaysExpanded = false, onLogoReset,
  hidePrivacyChip = false, // SPEC-82 collection_menu: the menu owns Privacy (the panel still renders here when opened)
}) {
  const { reducedMotion } = useDeviceProfile();
  // Same reasoning as the desktop rail: the privacy panel's content is fixed, so it measures itself
  // against the CC bar and shrinks to fit rather than falling back to a scroll — see ConsentPanel.js.
  const { width: viewportWidth } = useViewportSize();
  const railUnitPx = mobileUnitFor(viewportWidth);
  // Same derivation as the desktop rail: the wishlist is the source of truth for which bag rows
  // show a filled heart, so an agent-driven save fills it with no extra plumbing.
  const savedIds = React.useMemo(
    () => new Set((wishlistItems || []).map(i => i.id)),
    [wishlistItems]
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const cartOpen = expanded === 'CART';
  const wishlistOpen = expanded === 'WISHLIST';
  const privacyOpen = expanded === 'PRIVACY';
  const sectionOpen = cartOpen || wishlistOpen || privacyOpen;
  // An agent-driven `ui_update` sets `overlay` without going through the chip, so the menu has to
  // read as open whenever a section is — otherwise "show me my cart" would expand a panel under a
  // menu that still looked shut.
  // `alwaysExpanded` (Ophion demo only, see App.js) skips the hamburger entirely: the three
  // collapsed chips sit permanently at top-right, same order as the desktop rail, and there is no
  // menu state to track. Every other tenant keeps the hamburger unchanged.
  const open = alwaysExpanded || menuOpen || sectionOpen;

  const handleMenu = () => {
    if (!open) {
      setMenuOpen(true);
      return;
    }
    if (sectionOpen) onToggle(expanded); // collapse the section on the way out
    setMenuOpen(false);
  };

  return (
    <>
      {/* Owner 2026-10-13: an open section dims + blurs everything under it (PDP sheet, tags) on its
          own layer below the footer; a tap outside the panel closes it, same as its X. */}
      <MobilePanelScrim open={sectionOpen && !showIntro} onDismiss={() => onToggle(expanded)} reducedMotion={reducedMotion} />
      {/* `Rectangle 70` — the legibility scrim under the wordmark and chip. Behind both, above the
          film, and never interactive. */}
      <div
        aria-hidden="true"
        data-testid="mobile-top-scrim"
        className="absolute top-0 left-0 right-0 pointer-events-none"
        style={{ '--u': MOBILE_UNIT, height: mu(TOP_SCRIM.h), background: TOP_SCRIM.gradient }}
      />

      {/* Hidden while a section is expanded. The mobile panel is 1142 of 1216 authored px —
          93.9% of the frame — so it lands squarely over a centred wordmark, which the visual pass
          caught immediately and no unit test would have. Desktop has no such collision: its panel
          is 984 of 3840, a quarter of the frame, hard left of the mark. The collapsed chip stack
          is only 88 wide and clears the mark, so it is specifically the expanded state that hides
          it — the same thing a mobile nav overlay does everywhere else. */}
      {!sectionOpen && (
        <LogoResetButton
          src={wordmarkSrc}
          alt={brandName}
          className="absolute left-1/2 -translate-x-1/2 object-contain pointer-events-none"
          style={{ '--u': MOBILE_UNIT, top: safeTop(WORDMARK.top), width: mu(WORDMARK.w) }}
          onReset={onLogoReset}
        />
      )}

      <div
        className="absolute flex flex-col items-end pointer-events-auto"
        style={{
          // Inline, not just the Tailwind class above — see the matching comment in IconRail.js.
          // Same jsdom limitation, same fix: this now sits inside App.js's portal (inline
          // `pointer-events: none`), and only an inline reset is visible to a `getComputedStyle`
          // walk with no real stylesheet loaded.
          pointerEvents: 'auto',
          '--u': MOBILE_UNIT,
          top: safeTop(MENU_CHIP.top),
          right: safeRight(MENU_CHIP.rightInset),
          gap: mu(MENU_GAP),
        }}
      >
        {/* `alwaysExpanded` drops the hamburger outright — the three section chips below take its
            slot at the top of the stack instead, in the same cart/wishlist/privacy order the
            desktop rail uses. The recording indicator's hamburger-side branch above is moot with
            no menu chip to carry it; the privacy header below already shows it whenever privacy
            itself is collapsed, same as desktop. */}
        {!alwaysExpanded && (
          <MobileChip
            glyph={MENU_GLYPH}
            label={open ? 'Close menu' : 'Open menu'}
            expanded={open}
            onClick={handleMenu}
            // The strokes are authored at a fixed offset inside the chip (24 from the left, 32 from
            // the top), not centred — 32 above and 26 below. Placed, not centred, so the glyph sits
            // where 45:3291-45:3293 put it.
            glyphStyle={{
              position: 'absolute',
              left: mu(MENU_GLYPH.left),
              top: mu(MENU_GLYPH.top),
              width: mu(MENU_GLYPH.w),
              height: mu(MENU_GLYPH.h),
            }}
          >
            {/* VER-40: the recording disclosure rides on whichever control opens privacy — here that
                is the menu itself while it is shut. Same obligation, same indicator as the desktop
                rail (SPEC-26 R.6 OQ-R2). */}
            {recording && !open && (
              <span
                data-testid="recording-indicator"
                aria-label="Recording active"
                className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse ring-2 ring-black/40"
              />
            )}
          </MobileChip>
        )}

        <AnimatePresence initial={false}>
          {open && !showIntro && (cartOpen ? (
            <MobilePanel
              key="cart-panel"
              kind="CART"
              glyph={GLYPHS.bag}
              title="Shopping Bag"
              label="Open cart"
              items={cartItems}
              priceDisplay={priceDisplay}
              linePriceDisplay={linePriceDisplay}
              totalLabel="Subtotal"
              totalValue={cartTotalDisplay}
              unitCount={cartUnitCount}
              onCollapse={() => onToggle('CART')}
              onCheckout={onCheckout}
              onSelectItem={onSelectItem}
              onToggleSaved={onToggleSaved}
              onRemoveItem={onRemoveCartLine}
              onIncrementItem={onIncrementCartLine}
              onDecrementItem={onDecrementCartLine}
              savedIds={savedIds}
              reducedMotion={reducedMotion}
              scrollCommand={scrollCommand}
              onScrollStateChange={onScrollStateChange}
              // hidePrivacyChip is set exactly when collection_menu is on, i.e. when the top-left hamburger exists.
              clearsMenu={hidePrivacyChip}
            />
          ) : (
            <div key="cart-header" className="shrink-0">
              <MobileChip glyph={GLYPHS.bag} label="Open cart" expanded={false} onClick={() => onToggle('CART')}>
                <ChipBadge count={cartUnitCount ?? cartItems.length} />
              </MobileChip>
            </div>
          ))}

          {open && !showIntro && (wishlistOpen ? (
            <MobilePanel
              key="wishlist-panel"
              kind="WISHLIST"
              glyph={GLYPHS.saved}
              title="Favourites"
              label="Open wishlist"
              items={wishlistItems}
              priceDisplay={priceDisplay}
              onCollapse={() => onToggle('WISHLIST')}
              onSelectItem={onSelectItem}
              onToggleSaved={onToggleSaved}
              reducedMotion={reducedMotion}
              scrollCommand={scrollCommand}
              onScrollStateChange={onScrollStateChange}
              clearsMenu={hidePrivacyChip}
            />
          ) : (
            <div key="wishlist-header" className="shrink-0">
              <MobileChip glyph={GLYPHS.saved} label="Open wishlist" expanded={false} onClick={() => onToggle('WISHLIST')}>
                <ChipBadge count={wishlistItems.length} />
              </MobileChip>
            </div>
          ))}

          {open && (privacyOpen ? (
            <ConsentPanel
              key="privacy-panel"
              unit={mu}
              geometry={MOBILE_CONSENT}
              text={MOBILE_CONSENT_TEXT}
              panelW={MOBILE_PANEL.w}
              panelPad={MOBILE_PANEL.pad}
              panelRadius={MOBILE_PANEL.radius}
              baseUnitPx={railUnitPx}
              glyph={GLYPHS.privacy}
              consent={consent}
              onSave={(categories) => { onSaveConsent(categories); onToggle('PRIVACY'); }}
              onCollapse={() => onToggle('PRIVACY')}
              onOpenPrivacyPolicy={onOpenPrivacyPolicy}
            />
          ) : (hidePrivacyChip ? null : (
            <div key="privacy-header" className="shrink-0">
              <MobileChip glyph={GLYPHS.privacy} label={privacyLabel} onClick={() => onToggle('PRIVACY')}>
                {recording && (
                  <span
                    data-testid="recording-indicator"
                    aria-label="Recording active"
                    className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse ring-2 ring-black/40"
                  />
                )}
              </MobileChip>
            </div>
          )))}
        </AnimatePresence>
      </div>
    </>
  );
}

export default MobileChrome;
export { MobileChip, MobilePanel, MobileTile };
